import React, {useLayoutEffect, useRef, useState} from "react"
import {Box, IconButton, InputAdornment, TextField, useMediaQuery, useTheme} from "@mui/material"
import {SendRounded}                            from "@mui/icons-material"
import Messages                                 from "../prepkit/chat/Messages"
import QuickStartActions                        from "./QuickStartActions"
import {hasStreamingMessage}                    from "../../util/prepkit-utils"

/*
 * Desktop right-hand column of the question screen — a full-height chat surface:
 * "ASK BIRDSY" header at the top, then a scrollable pane holding the chat transcript
 * followed by the Quick-Start action buttons (which swap on answer state), and the ask
 * input + Send pinned to the bottom of the screen.
 *
 * Small screens do not use this panel; they keep the fixed bottom QuestionInput bar.
 */

const AskBirdsyPanel = ({
	contextId,
	messages,
	actions,
	onSend,
	onShowCorrect,
	messagesEndRef,
	contentMaxWidth = '100%'
}) => {

	const theme = useTheme()
	const custom = theme.custom
	const [body, setBody] = useState("")

	const canSend = body.trim().length > 0

	const handleSend = () => {
		if (!canSend) return
		setBody("")
		onSend(body)
	}

	// `type` tells the API which config prompt to use, and lets the caller mark it used (062)
	const handleAction = (a) => {
		onSend({displayText: a.displayText, type: a.type})
	}

	const hasMessages = Boolean(messages && messages.length > 0)

	/*
	 * The input moves from under the Quick-Start buttons to the pinned bottom row on the first
	 * message. Animate that as a FLIP — measure where it was, start it there, transition to its
	 * new place — because the distance depends on the panel height and how many buttons showed.
	 */
	const inputWrapRef = useRef(null)
	const lastRectRef = useRef(null)
	const [flip, setFlip] = useState(0)
	const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

	useLayoutEffect(() => {
		const node = inputWrapRef.current
		if (!node) return

		const rect = node.getBoundingClientRect()
		const previous = lastRectRef.current
		lastRectRef.current = rect

		// nothing to animate on first render, tiny shifts, or when the user opts out of motion
		if (!previous || reduceMotion) return

		const delta = previous.top - rect.top
		if (Math.abs(delta) < 1) return

		setFlip(delta)
		requestAnimationFrame(() => setFlip(0))
	}, [hasMessages, reduceMotion])

	/*
	 * The scrolling element spans the whole pane so its scrollbar sits at the right edge of the
	 * window rather than floating mid-page. Each row's content is capped and pushed to the seam
	 * instead, which is what used to be done by a wrapper around the whole panel.
	 */
	const laneSx = {
		width: '100%',
		maxWidth: contentMaxWidth,
		mr: 'auto',
		px: 3,
	}

	const inputWrapSx = {
		flexShrink: 0,
		transform: `translateY(${flip}px)`,
		// no transition on the jump back to the old spot, only on the settle into the new one
		transition: flip === 0 ? 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
	}

	const isStreaming = hasStreamingMessage(messages)

	const askInput = (
			<TextField
				autoComplete="off"
				name="ask-birdsy"
				fullWidth={true}
				size="small"
				placeholder="✦ Ask Birdsy Anything"
				value={body}
				onChange={(event) => setBody(event.target.value)}
				onKeyUp={(event) => { if (event.key === 'Enter') handleSend() }}
				InputProps={{
					// send lives in the field, same as the mobile chat input
					endAdornment: <InputAdornment position="end">
						<IconButton
							aria-label="Send"
							size="small"
							edge="end"
							disabled={!canSend}
							onClick={handleSend}
							sx={{color: canSend ? custom.indigo.primary : custom.text.muted}}
						>
							<SendRounded/>
						</IconButton>
					</InputAdornment>,
				}}
				sx={{
					'& .MuiOutlinedInput-root': {
						borderRadius: `${custom.radius.md}px`,
						background: custom.bg.cardInner,
						// bigger than the token scale on purpose — this is the panel's primary input
						fontSize: '16px',
						// the 4px outline needs room, or the text sits on top of it
						'& input': {padding: '16px 14px 16px 22px'},
						// always wears the indigo outline, wherever it sits, so it draws the eye
						// (same width in every state, including focus — MUI would otherwise re-set it)
						'& fieldset':             {borderColor: custom.indigo.border, borderWidth: '4px'},
						'&:hover fieldset':       {borderColor: custom.indigo.border, borderWidth: '4px'},
						'&.Mui-focused fieldset': {borderColor: custom.indigo.border, borderWidth: '4px'},
					},
				}}
			/>
	)

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: 2,
			pt: 3,
			// the pinned input row owns the bottom band so it lines up with the nav row
			pb: 0,
			// the column is the narrower of the two panes — chat + labels wrap inside it
			minWidth: 0,
			overflowWrap: 'anywhere',
			// fills the fixed-height column so the ask input sits at its bottom edge
			height: '100%',
			minHeight: 0,
			// no card chrome: the column itself provides the (light gray) background
		}}>
			<Box sx={{
				...laneSx,
				display: 'flex',
				alignItems: 'center',
				gap: 0.75,
				flexShrink: 0,
				fontSize: custom.fontSize.xs,
				fontWeight: custom.fontWeight.semibold,
				letterSpacing: '0.08em',
				textTransform: 'uppercase',
				color: custom.indigo.border,
			}}>
			{/* the mark is gold; the wordmark keeps the indigo */}
				<Box component="span" sx={{color: custom.status.amber}}>✦</Box> Ask Birdsy
			</Box>

			{/* messages first, then the Quick-Start actions — both scroll together */}
			<Box sx={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
			<Box sx={{...laneSx, display: 'flex', flexDirection: 'column', gap: 2}}>
				{
					hasMessages &&
					<Box>
						<Messages
							contextId={contextId}
							messages={messages}
							showMessagesAfter={0}
						/>
						<div ref={messagesEndRef}/>
					</Box>
				}

				{/* actions drop out while Birdsy is answering, then fade back in */}
				<QuickStartActions
					actions={actions}
					onAction={handleAction}
					isStreaming={isStreaming}
					hint={
						/* the "or" prompt only makes sense before the conversation starts */
						!hasMessages &&
						<Box sx={{
							fontSize: custom.fontSize.xs,
							fontWeight: custom.fontWeight.medium,
							color: custom.text.muted,
							px: 0.5,
						}}>
							Or, select a Quick-Start option below
						</Box>
					}
				/>

				{
					/* before the conversation starts the input sits with the actions, where the
					 * user is already looking */
					!hasMessages &&
					<Box ref={inputWrapRef} sx={inputWrapSx}>{askInput}</Box>
				}
			</Box>
			</Box>

			{
				/* once the conversation starts the input pins to the bottom, on the same 64px band
				 * as the left column's navigation row */
				hasMessages &&
				<Box ref={inputWrapRef} sx={{...laneSx, ...inputWrapSx, minHeight: 64, display: 'flex', alignItems: 'center'}}>
					{askInput}
				</Box>
			}
		</Box>
	)
}

export default AskBirdsyPanel
